import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper
} from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SchoolIcon from "@mui/icons-material/School";
import WorkIcon from "@mui/icons-material/Work";
import LanguageIcon from "@mui/icons-material/Language";
import ComputerIcon from "@mui/icons-material/Computer";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import StarIcon from "@mui/icons-material/Star";
import LinkIcon from "@mui/icons-material/Link";
import PersonIcon from "@mui/icons-material/Person";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import DriveEtaIcon from "@mui/icons-material/DriveEta";
import { ChevronLeft, ChevronRight, DragIndicator, Reorder } from "@mui/icons-material";

export const resumeMeta = {
  hasPhoto: true,
  columns: 2,
};

// Fixed pixel dimensions per page size
const PAGE_SIZES = {
  A4:        { width: 794,  height: 1123 },
  Letter:    { width: 816,  height: 1056 },
  Legal:     { width: 816,  height: 1344 },
  A3:        { width: 1123, height: 1587 },
  Executive: { width: 696,  height: 1010 },
};

const defaultData = {
  firstName: "Donna",
  lastName: "Stroupe",
  currentPosition: "Business Student",
  phone: "+1 (123) 456-7890",
  email: "donna.stroupe@email.com",
  city: "123 Anywhere St., Any City",
  website: "",
  linkedIn: "",
  drivingLicence: "",
  profile: "",
};

const defaultLeftSectionOrder  = ["contact", "education", "skills"];
const defaultRightSectionOrder = [
  "profile", "workExperience", "languages", "accomplishments",
  "certifications", "softwareSkills", "volunteering", "interests",
  "websites", "references", "additionalInfo",
];

// Global overflow-safe style applied everywhere text can be long
const OVF = {
  wordBreak: "break-word",
  overflowWrap: "break-word",
  whiteSpace: "pre-wrap",
  minWidth: 0,
};

const Resume2 = ({
  color         = "#791115ff",
  font          = "Arial",
  fontSize      = "14px",
  headingSize   = "24px",
  fontStyle     = "normal",
  sectionSpacing   = 50,
  paragraphSpacing = 30,
  lineSpacing      = 20,
  topBottomMargin  = 40,
  sideMargins      = 40,
  lineWeight       = 1,
  pageSize         = "A4",
  formData         = {},
  photo            = "",
  workExperiences  = [],
  exporting        = false,
  enableDragDrop   = true,
}) => {
  const page   = PAGE_SIZES[pageSize] || PAGE_SIZES.A4;
  const data   = { ...defaultData, ...formData };
  const SIDE_W = Math.round(page.width * 0.30);
  const MAIN_W = page.width - SIDE_W;

  // Convert slider values to usable CSS/px values
  const secGap  = `${Math.max(6,  sectionSpacing   / 8)}px`;
  const paraGap = `${Math.max(3,  paragraphSpacing / 10)}px`;
  const lh      =  Math.max(1.2,  lineSpacing      / 14);

  const [currentPage,          setCurrentPage]          = useState(0);
  const [pages,                setPages]                = useState([]);   // [{ rightIds: string[] }]
  const mainContentRef                                  = useRef(null);   // hidden measure container

  const [educationEntries,     setEducationEntries]      = useState([]);
  const [savedSkills,          setSavedSkills]           = useState([]);
  const [savedSummary,         setSavedSummary]          = useState("");
  const [savedAdditionalInfo,  setSavedAdditionalInfo]   = useState([]);
  const [savedLanguages,       setSavedLanguages]        = useState([]);
  const [savedAccomplishments, setSavedAccomplishments]  = useState([]);
  const [savedCertifications,  setSavedCertifications]   = useState([]);
  const [savedReferences,      setSavedReferences]       = useState([]);
  const [softwareSkills,       setSoftwareSkills]        = useState([]);
  const [savedVolunteering,    setSavedVolunteering]      = useState([]);
  const [savedInterests,       setSavedInterests]        = useState([]);
  const [savedWebsites,        setSavedWebsites]         = useState([]);
  const [customSections,       setCustomSections]        = useState({});
  const [leftSectionOrder,     setLeftSectionOrder]      = useState(defaultLeftSectionOrder);
  const [rightSectionOrder,    setRightSectionOrder]     = useState(defaultRightSectionOrder);
  const [dragOverIndex,        setDragOverIndex]         = useState(null);
  const [dragOverSide,         setDragOverSide]          = useState(null);
  const [arrangeDialogOpen,    setArrangeDialogOpen]     = useState(false);

  // ── Load all data from localStorage ──────────────────────────────────────
  useEffect(() => {
    try {
      const edu = localStorage.getItem("educationEntries");
      if (edu) setEducationEntries(JSON.parse(edu));

      const sk = localStorage.getItem("skills");
      if (sk) setSavedSkills(JSON.parse(sk));

      const sum = JSON.parse(localStorage.getItem("summaries") || "[]");
      if (sum.length) setSavedSummary(sum[sum.length - 1]);

      setSavedAdditionalInfo( JSON.parse(localStorage.getItem("additionalInfo")       || "[]"));
      setSavedLanguages(      JSON.parse(localStorage.getItem("languagesList")         || "[]"));
      setSavedAccomplishments(JSON.parse(localStorage.getItem("accomplishmentsList")   || "[]"));
      setSavedCertifications( JSON.parse(localStorage.getItem("certificationsList")    || "[]"));
      setSavedReferences(     JSON.parse(localStorage.getItem("referencesList")        || "[]"));
      setSoftwareSkills(      JSON.parse(localStorage.getItem("softwareSkills")        || "[]"));
      setSavedVolunteering(   JSON.parse(localStorage.getItem("volunteering")          || "[]"));
      setSavedInterests(      JSON.parse(localStorage.getItem("interests")             || "[]"));
      setSavedWebsites(       JSON.parse(localStorage.getItem("websites")              || "[]"));

      const userId = localStorage.getItem("userId");
      const sectionsData = {};
      if (userId) {
        const curr = JSON.parse(localStorage.getItem(`current_custom_sections_${userId}`) || "[]");
        curr.forEach(name => {
          try {
            const raw = localStorage.getItem(`custom_${userId}_${name}`);
            if (raw) {
              const valid = (JSON.parse(raw).items || []).filter(i => i && i.trim());
              if (valid.length) sectionsData[name] = valid;
            }
          } catch {}
        });
      }
      setCustomSections(sectionsData);

      const lo = localStorage.getItem("resume2LeftSectionOrder");
      if (lo) setLeftSectionOrder(JSON.parse(lo).filter(s => !s.startsWith("custom_")));

      const ro = localStorage.getItem("resume2RightSectionOrder");
      const customIds = Object.keys(sectionsData).map(n => `custom_${n}`);
      if (ro) {
        const filtered = JSON.parse(ro).filter(s => !s.startsWith("custom_"));
        setRightSectionOrder([...filtered, ...customIds]);
      } else {
        setRightSectionOrder([...defaultRightSectionOrder, ...customIds]);
      }
    } catch (e) {
      console.error("Resume2 load error:", e);
    }
  }, []);

  const formatDate = (m, y) => (m && y ? `${m} ${y}` : "");

  // ── Drag & drop ───────────────────────────────────────────────────────────
  const mkStart = (side, order) => (e, idx) => {
    e.dataTransfer.setData("text/plain", JSON.stringify({ idx, side, id: order[idx] }));
    e.currentTarget.style.opacity = "0.5";
  };
  const onOver  = (e, idx, side) => { e.preventDefault(); setDragOverIndex(idx); setDragOverSide(side); };
  const onLeave = () => { setDragOverIndex(null); setDragOverSide(null); };
  const onEnd   = e  => { e.currentTarget.style.opacity = "1"; onLeave(); };

  const onDrop = (e, targetIdx, targetSide) => {
    e.preventDefault();
    const { idx: srcIdx, side: srcSide, id } = JSON.parse(e.dataTransfer.getData("text/plain"));
    let lo = [...leftSectionOrder], ro = [...rightSectionOrder];
    if (srcSide === targetSide) {
      const arr = srcSide === "left" ? lo : ro;
      const [m] = arr.splice(srcIdx, 1);
      arr.splice(targetIdx, 0, m);
    } else {
      (srcSide === "left" ? lo : ro).splice(srcIdx, 1);
      (targetSide === "left" ? lo : ro).splice(targetIdx, 0, id);
    }
    setLeftSectionOrder(lo);  localStorage.setItem("resume2LeftSectionOrder",  JSON.stringify(lo));
    setRightSectionOrder(ro); localStorage.setItem("resume2RightSectionOrder", JSON.stringify(ro));
    onLeave();
    e.currentTarget.style.opacity = "1";
  };

  const resetAll = () => {
    setLeftSectionOrder(defaultLeftSectionOrder);
    setRightSectionOrder(defaultRightSectionOrder);
    localStorage.setItem("resume2LeftSectionOrder",  JSON.stringify(defaultLeftSectionOrder));
    localStorage.setItem("resume2RightSectionOrder", JSON.stringify(defaultRightSectionOrder));
  };

  // ── Shared text style builders ────────────────────────────────────────────
  const sbTxt = {
    color: "white", fontSize, fontFamily: font, fontStyle,
    lineHeight: lh, ...OVF,
  };
  const sbHd = {
    fontSize: headingSize, fontWeight: "bold", fontFamily: font,
    color: "white", lineHeight: lh, mb: paraGap, ...OVF,
  };
  const mnTxt = {
    fontSize, fontFamily: font, fontStyle,
    lineHeight: lh, color: "inherit", ...OVF,
  };
  const mnHd = {
    fontSize: headingSize, fontWeight: "bold", fontFamily: font,
    lineHeight: lh, mb: paraGap, ...OVF,
  };

  // ── Education entries helper ──────────────────────────────────────────────
  const getEduEntries = () => {
    try {
      const fer = localStorage.getItem("educationEntriesForResume");
      if (fer) return JSON.parse(fer);
      if (educationEntries.length) {
        return educationEntries.map(e =>
          e.educationLevel === "school"
            ? { id: e.id, degree: `${e.standard || ""} Standard`, fieldOfStudy: e.stream || "General Education", schoolName: e.schoolName || "", schoolLocation: e.schoolLocation || "", gradMonth: "", gradYear: "", additionalCoursework: [e.district, e.percentage && `Percentage: ${e.percentage}%`].filter(Boolean).join(" | ") }
            : { id: e.id, degree: `${e.degreeName || ""}${e.programType ? ` (${e.programType})` : ""}`, fieldOfStudy: e.specialization || e.degreeType || "", schoolName: e.collegeName || "", schoolLocation: e.collegeLocation || "", gradMonth: e.gradMonth || "", gradYear: e.gradYear || "", additionalCoursework: [e.cgpa && `CGPA: ${e.cgpa}`, e.percentage && `Percentage: ${e.percentage}%`, e.grade && `Grade: ${e.grade}`, e.additionalCoursework].filter(Boolean).join(" | ") }
        );
      }
    } catch {}
    return [];
  };

  // ── LEFT SIDEBAR sections ─────────────────────────────────────────────────
  const renderLeft = (id) => {
    switch (id) {
      case "contact":
        return (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%", mb: secGap }}>
            <Avatar
              src={photo || "https://via.placeholder.com/120"}
              sx={{ width: 110, height: 110, mb: "10px", border: "3px solid rgba(255,255,255,0.5)" }}
            />
            <Divider sx={{ bgcolor: "rgba(255,255,255,0.35)", width: "100%", mb: "8px", borderBottomWidth: lineWeight }} />
            <Typography sx={{ ...sbHd, textAlign: "center" }}>CONTACT ME</Typography>
            {[
              { show: data.phone,         icon: <PhoneIcon    sx={{ fontSize: 14, color: "white",   flexShrink: 0 }} />, val: data.phone },
              { show: data.email,         icon: <EmailIcon    sx={{ fontSize: 14, color: "white",   flexShrink: 0 }} />, val: data.email },
              { show: data.city,          icon: <LocationOnIcon sx={{ fontSize: 14, color: "white", flexShrink: 0 }} />, val: data.city },
              { show: data.website,       icon: <LanguageIcon sx={{ fontSize: 14, color: "white",   flexShrink: 0 }} />, val: data.website },
              { show: data.linkedIn,      icon: <LinkedInIcon sx={{ fontSize: 14, color: "#90caf9", flexShrink: 0 }} />, val: data.linkedIn },
              { show: data.drivingLicence,icon: <DriveEtaIcon sx={{ fontSize: 14, color: "white",   flexShrink: 0 }} />, val: `DL: ${data.drivingLicence}` },
            ].filter(r => r.show).map((row, i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "flex-start", mb: paraGap, width: "100%" }}>
                <Box sx={{ flexShrink: 0, mr: "5px", mt: "2px" }}>{row.icon}</Box>
                <Typography sx={sbTxt}>{row.val}</Typography>
              </Box>
            ))}
          </Box>
        );

      case "education": {
        const entries = getEduEntries();
        return (
          <Box sx={{ width: "100%", mb: secGap }}>
            <Divider sx={{ bgcolor: "rgba(255,255,255,0.35)", width: "100%", mb: "8px", borderBottomWidth: lineWeight }} />
            <Typography sx={{ ...sbHd, display: "flex", alignItems: "center", gap: "5px" }}>
              <SchoolIcon sx={{ fontSize: 16 }} /> EDUCATION
            </Typography>
            {entries.length ? entries.map((e, i) => (
              <Box key={e.id || i} sx={{ mb: paraGap }}>
                <Typography sx={{ ...sbTxt, fontWeight: "bold" }}>{e.degree}{e.fieldOfStudy ? ` - ${e.fieldOfStudy}` : ""}</Typography>
                <Typography sx={sbTxt}>{e.schoolName}{e.schoolLocation ? `, ${e.schoolLocation}` : ""}</Typography>
                {(e.gradMonth || e.gradYear) && <Typography sx={{ ...sbTxt, fontStyle: "italic" }}>{formatDate(e.gradMonth, e.gradYear)}</Typography>}
                {e.additionalCoursework && <Typography sx={sbTxt}>{e.additionalCoursework}</Typography>}
              </Box>
            )) : <Typography sx={sbTxt}>No education entries added yet.</Typography>}
          </Box>
        );
      }

      case "skills":
        return (
          <Box sx={{ width: "100%", mb: secGap }}>
            <Divider sx={{ bgcolor: "rgba(255,255,255,0.35)", width: "100%", mb: "8px", borderBottomWidth: lineWeight }} />
            <Typography sx={{ ...sbHd, display: "flex", alignItems: "center", gap: "5px" }}>
              <StarIcon sx={{ fontSize: 16 }} /> SKILLS
            </Typography>
            {savedSkills.length ? savedSkills.map((sk, i) => (
              <Box key={i} sx={{ mb: paraGap }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography sx={{ ...sbTxt, flex: 1 }}>{sk.name || sk.category || ""}</Typography>
                  {sk.rating != null && <Typography sx={{ ...sbTxt, ml: "6px", flexShrink: 0 }}>{sk.rating}%</Typography>}
                </Box>
                {sk.rating != null && (
                  <Box sx={{ height: "3px", bgcolor: "rgba(255,255,255,0.25)", borderRadius: "2px", mt: "2px" }}>
                    <Box sx={{ height: "100%", width: `${sk.rating}%`, bgcolor: "white", borderRadius: "2px" }} />
                  </Box>
                )}
              </Box>
            )) : <Typography sx={sbTxt}>No skills added yet.</Typography>}
          </Box>
        );

      default:
        return (
          <Box sx={{ width: "100%", mb: secGap }}>
            {renderRight(id, true)}
          </Box>
        );
    }
  };

  // ── RIGHT MAIN sections ───────────────────────────────────────────────────
  // filterIds: when provided, skip sections not in list (pagination)
  const renderRight = (id, inSidebar = false, filterIds = null) => {
    if (filterIds !== null && !filterIds.includes(id)) return null;

    const tSx  = inSidebar ? sbTxt : mnTxt;
    const hSx  = inSidebar ? sbHd  : mnHd;
    const iCol = inSidebar ? "white" : color;

    const SectionHead = ({ icon: Icon, label }) => (
      <Box sx={{ display: "flex", alignItems: "center", mb: paraGap }}>
        <Icon sx={{ color: iCol, fontSize: headingSize, mr: "6px", flexShrink: 0 }} />
        <Typography sx={{ ...hSx, mb: 0, color: iCol }}>{label}</Typography>
      </Box>
    );

    const HDivider = () => (
      <Divider sx={{ mb: paraGap, borderColor: inSidebar ? "rgba(255,255,255,0.25)" : color, borderBottomWidth: lineWeight }} />
    );

    // Wrapper adds class + data-section-id for pagination measurement
    const Wrap = ({ children }) => (
      <Box
        className="resume-section"
        data-section-id={id}
        sx={{ mb: secGap }}
      >
        {children}
      </Box>
    );

    switch (id) {
      case "profile":
        return (savedSummary || data.profile) ? (
          <Wrap key="profile">
            <SectionHead icon={PersonIcon} label="SUMMARY" />
            <HDivider />
            <Typography sx={tSx}>{savedSummary || data.profile}</Typography>
          </Wrap>
        ) : null;

      case "workExperience":
        return workExperiences?.length ? (
          <Wrap key="workExperience">
            <SectionHead icon={WorkIcon} label="WORK EXPERIENCE" />
            <HDivider />
            {workExperiences.map((w, i) => (
              <Box key={i} sx={{ mb: paraGap }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "2px" }}>
                  <Typography sx={{ ...tSx, fontWeight: "bold", flex: 1 }}>{w.jobTitle} @ {w.employer}</Typography>
                  <Typography sx={{ ...tSx, whiteSpace: "nowrap", flexShrink: 0, ml: "6px" }}>
                    {formatDate(w.startMonth, w.startYear)} – {w.current ? "Present" : formatDate(w.endMonth, w.endYear)}
                  </Typography>
                </Box>
                <Typography sx={{ ...tSx, fontStyle: "italic" }}>
                  {w.companyName}{w.location ? ` | ${w.location}` : ""}
                </Typography>
                {w.description && (
                  <Box sx={{ mt: "3px", ...tSx,
                    "& strong": { fontWeight: "bold" }, "& em": { fontStyle: "italic" },
                    "& u": { textDecoration: "underline" },
                  }}
                    dangerouslySetInnerHTML={{ __html: w.description
                      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
                      .replace(/\*(.*?)\*/g, "<em>$1</em>")
                      .replace(/<u>(.*?)<\/u>/g, "<u>$1</u>")
                      .replace(/\n/g, "<br/>") }}
                  />
                )}
              </Box>
            ))}
          </Wrap>
        ) : null;

      case "contact":
        return (
          <Wrap key="contact">
            <SectionHead icon={PersonIcon} label="CONTACT ME" />
            <HDivider />
            {!inSidebar && (
              <Avatar src={photo || "https://via.placeholder.com/120"}
                sx={{ width: 100, height: 100, mb: "10px", border: `3px solid ${color}`, alignSelf: "center" }} />
            )}
            {[
              { show: data.phone,    icon: <PhoneIcon    sx={{ fontSize: 14, color: iCol, flexShrink: 0 }} />, val: data.phone },
              { show: data.email,    icon: <EmailIcon    sx={{ fontSize: 14, color: iCol, flexShrink: 0 }} />, val: data.email },
              { show: data.city,     icon: <LocationOnIcon sx={{ fontSize: 14, color: iCol, flexShrink: 0 }} />, val: data.city },
              { show: data.website,  icon: <LanguageIcon sx={{ fontSize: 14, color: iCol, flexShrink: 0 }} />, val: data.website },
              { show: data.linkedIn, icon: <LinkedInIcon sx={{ fontSize: 14, color: inSidebar ? "#90caf9" : "#0077b5", flexShrink: 0 }} />, val: data.linkedIn },
              { show: data.drivingLicence, icon: <DriveEtaIcon sx={{ fontSize: 14, color: iCol, flexShrink: 0 }} />, val: `DL: ${data.drivingLicence}` },
            ].filter(r => r.show).map((row, i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "flex-start", mb: paraGap }}>
                <Box sx={{ flexShrink: 0, mr: "5px", mt: "2px" }}>{row.icon}</Box>
                <Typography sx={tSx}>{row.val}</Typography>
              </Box>
            ))}
          </Wrap>
        );

      case "education": {
        const entries = getEduEntries();
        return (
          <Wrap key="education">
            <SectionHead icon={SchoolIcon} label="EDUCATION" />
            <HDivider />
            {entries.length ? entries.map((e, i) => (
              <Box key={e.id || i} sx={{ mb: paraGap }}>
                <Typography sx={{ ...tSx, fontWeight: "bold" }}>{e.degree}{e.fieldOfStudy ? ` - ${e.fieldOfStudy}` : ""}</Typography>
                <Typography sx={tSx}>{e.schoolName}{e.schoolLocation ? `, ${e.schoolLocation}` : ""}</Typography>
                {(e.gradMonth || e.gradYear) && <Typography sx={{ ...tSx, fontStyle: "italic" }}>{formatDate(e.gradMonth, e.gradYear)}</Typography>}
                {e.additionalCoursework && <Typography sx={tSx}>{e.additionalCoursework}</Typography>}
              </Box>
            )) : <Typography sx={tSx}>No education entries added yet.</Typography>}
          </Wrap>
        );
      }

      case "skills":
        return savedSkills.length ? (
          <Wrap key="skills">
            <SectionHead icon={StarIcon} label="SKILLS" />
            <HDivider />
            {savedSkills.map((sk, i) => (
              <Box key={i} sx={{ mb: paraGap }}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ ...tSx, flex: 1 }}>{sk.name || sk.category || ""}</Typography>
                  {sk.rating != null && <Typography sx={{ ...tSx, ml: "6px", flexShrink: 0 }}>{sk.rating}%</Typography>}
                </Box>
                {sk.rating != null && (
                  <Box sx={{ height: "3px", bgcolor: inSidebar ? "rgba(255,255,255,0.25)" : "#e0e0e0", borderRadius: "2px", mt: "2px" }}>
                    <Box sx={{ height: "100%", width: `${sk.rating}%`, bgcolor: color, borderRadius: "2px" }} />
                  </Box>
                )}
              </Box>
            ))}
          </Wrap>
        ) : null;

      case "languages":
        return savedLanguages.length ? (
          <Wrap key="languages">
            <SectionHead icon={LanguageIcon} label="LANGUAGES" />
            <HDivider />
            {savedLanguages.map((l, i) => (
              <Typography key={i} sx={{ ...tSx, mb: paraGap }}>• {l.name}{l.level ? ` (${l.level})` : ""}</Typography>
            ))}
          </Wrap>
        ) : null;

      case "accomplishments":
        return savedAccomplishments.length ? (
          <Wrap key="accomplishments">
            <SectionHead icon={StarIcon} label="ACCOMPLISHMENTS" />
            <HDivider />
            {savedAccomplishments.map((a, i) => (
              <Typography key={i} sx={{ ...tSx, mb: paraGap }}>• {a}</Typography>
            ))}
          </Wrap>
        ) : null;

      case "certifications":
        return savedCertifications.length ? (
          <Wrap key="certifications">
            <SectionHead icon={SchoolIcon} label="CERTIFICATIONS" />
            <HDivider />
            {savedCertifications.map((c, i) => (
              <Typography key={i} sx={{ ...tSx, mb: paraGap }}>
                • {c.name}{c.provider ? ` – ${c.provider}` : ""}{c.year ? ` (${c.year})` : ""}
              </Typography>
            ))}
          </Wrap>
        ) : null;

      case "softwareSkills":
        return softwareSkills.length ? (
          <Wrap key="softwareSkills">
            <SectionHead icon={ComputerIcon} label="SOFTWARE SKILLS" />
            <HDivider />
            {softwareSkills.map((s, i) => (
              <Box key={i} sx={{ mb: paraGap }}>
                <Typography sx={tSx}>{s.name} — {s.level}%</Typography>
                <Box sx={{ height: "3px", bgcolor: inSidebar ? "rgba(255,255,255,0.25)" : "#e0e0e0", borderRadius: "2px", mt: "2px" }}>
                  <Box sx={{ height: "100%", width: `${s.level}%`, bgcolor: inSidebar ? "white" : color, borderRadius: "2px" }} />
                </Box>
              </Box>
            ))}
          </Wrap>
        ) : null;

      case "volunteering":
        return savedVolunteering.length ? (
          <Wrap key="volunteering">
            <SectionHead icon={VolunteerActivismIcon} label="VOLUNTEERING" />
            <HDivider />
            {savedVolunteering.map((v, i) => (
              <Box key={i} sx={{ mb: paraGap }}>
                <Typography sx={{ ...tSx, fontWeight: "bold" }}>{v.subtopic}</Typography>
                <Typography sx={{ ...tSx, fontStyle: "italic", opacity: 0.75 }}>{v.fromDate} - {v.toDate}</Typography>
                <Typography sx={tSx}>{v.content}</Typography>
              </Box>
            ))}
          </Wrap>
        ) : null;

      case "interests":
        return savedInterests.length ? (
          <Wrap key="interests">
            <SectionHead icon={ComputerIcon} label="INTERESTS" />
            <HDivider />
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "3px 12px" }}>
              {savedInterests.map((item, i) => (
                <Typography key={i} sx={tSx}>• {item}</Typography>
              ))}
            </Box>
          </Wrap>
        ) : null;

      case "websites":
        return savedWebsites.length ? (
          <Wrap key="websites">
            <SectionHead icon={LinkIcon} label="WEBSITES / PROFILES" />
            <HDivider />
            {savedWebsites.map((s, i) => (
              <Typography key={i} sx={{ ...tSx, mb: paraGap }}>
                • <a href={s.url?.startsWith("http") ? s.url : `https://${s.url}`}
                  target="_blank" rel="noopener noreferrer"
                  style={{ color: inSidebar ? "#90caf9" : color, textDecoration: "none", wordBreak: "break-all" }}>
                  {s.name || s.url}
                </a>
              </Typography>
            ))}
          </Wrap>
        ) : null;

      case "references":
        return savedReferences.length ? (
          <Wrap key="references">
            <SectionHead icon={PersonIcon} label="REFERENCES" />
            <HDivider />
            {savedReferences.map((ref, i) => (
              <Typography key={i} sx={{ ...tSx, mb: paraGap }}>{ref}</Typography>
            ))}
          </Wrap>
        ) : null;

      case "additionalInfo":
        return savedAdditionalInfo.length ? (
          <Wrap key="additionalInfo">
            <SectionHead icon={ComputerIcon} label="ADDITIONAL INFORMATION" />
            <HDivider />
            {savedAdditionalInfo.map((info, i) => (
              <Typography key={i} sx={{ ...tSx, mb: paraGap }}>{info}</Typography>
            ))}
          </Wrap>
        ) : null;

      default:
        if (id.startsWith("custom_")) {
          const name  = id.replace("custom_", "");
          const items = customSections[name] || [];
          return items.length ? (
            <Wrap key={id}>
              <SectionHead icon={ComputerIcon} label={name.toUpperCase()} />
              <HDivider />
              {items.map((item, i) => (
                <Typography key={i} sx={{ ...tSx, mb: paraGap }}>• {item}</Typography>
              ))}
            </Wrap>
          ) : null;
        }
        return null;
    }
  };

  // ── Pagination ────────────────────────────────────────────────────────────
  const availableHeight = page.height - (2 * topBottomMargin);

  const paginateContent = useCallback(() => {
    if (!mainContentRef.current) return;
    const sections = Array.from(mainContentRef.current.querySelectorAll(".resume-section"));
    if (sections.length === 0) { setPages([{ rightIds: [] }]); return; }

    const newPages = [];
    let currentIds = [];
    let currentHeight = 0;

    sections.forEach(section => {
      const id = section.getAttribute("data-section-id");
      const h  = section.offsetHeight;
      if (currentHeight + h > availableHeight && currentIds.length > 0) {
        newPages.push({ rightIds: currentIds });
        currentIds = [id];
        currentHeight = h;
      } else {
        currentIds.push(id);
        currentHeight += h;
      }
    });
    if (currentIds.length > 0) newPages.push({ rightIds: currentIds });
    setPages(newPages);
  }, [
    availableHeight, rightSectionOrder,
    savedSummary, workExperiences, savedLanguages, savedAccomplishments,
    savedCertifications, softwareSkills, savedVolunteering, savedInterests,
    savedWebsites, savedReferences, savedAdditionalInfo, customSections, educationEntries,
    fontSize, headingSize, font, fontStyle, lineSpacing, paragraphSpacing, sectionSpacing,
  ]);

  useEffect(() => {
    const id = setTimeout(() => paginateContent(), 400);
    return () => clearTimeout(id);
  }, [paginateContent]);

  const getSectionTitle = id => {
    const map = {
      contact: "Contact Me", education: "Education", skills: "Skills",
      profile: "Summary", workExperience: "Work Experience", languages: "Languages",
      accomplishments: "Accomplishments", certifications: "Certifications",
      softwareSkills: "Software Skills", volunteering: "Volunteering",
      interests: "Interests", websites: "Websites/Profiles",
      references: "References", additionalInfo: "Additional Info",
    };
    return id.startsWith("custom_") ? id.replace("custom_", "").toUpperCase() : (map[id] || id);
  };

  // ── Drag panel inside dialog ──────────────────────────────────────────────
  const DragPanel = ({ side, sections }) => (
    <Box sx={{ flex: 1 }}>
      <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
        {side === "left" ? "Left Sidebar" : "Main Content"} — {sections.length} sections
      </Typography>
      <Box sx={{ maxHeight: 400, overflowY: "auto", border: "1px solid #e0e0e0", borderRadius: 1, p: 1 }}>
        {sections.length ? sections.map((sid, idx) => (
          <Paper key={sid} draggable
            onDragStart={e => mkStart(side, sections)(e, idx)}
            onDragOver={e  => onOver(e, idx, side)}
            onDragLeave={onLeave}
            onDrop={e      => onDrop(e, idx, side)}
            onDragEnd={onEnd}
            sx={{ p: "10px 14px", mb: 1, display: "flex", alignItems: "center",
              bgcolor: dragOverIndex === idx && dragOverSide === side ? "#f0f0f0" : "white",
              border:  dragOverIndex === idx && dragOverSide === side ? `2px dashed ${color}` : "1px solid #ddd",
              cursor: "grab" }}>
            <DragIndicator color="action" sx={{ mr: 1 }} />
            <Typography sx={{ flex: 1, fontSize: "13px" }}>{getSectionTitle(sid)}</Typography>
            <Typography variant="body2" color="textSecondary">{idx + 1}</Typography>
          </Paper>
        )) : (
          <Typography sx={{ p: 2, textAlign: "center", color: "text.secondary", fontSize: "13px" }}>
            Drag sections here from the other side.
          </Typography>
        )}
      </Box>
      <Button size="small" variant="outlined" sx={{ mt: 1 }}
        onClick={() => {
          if (side === "left") {
            setLeftSectionOrder(defaultLeftSectionOrder);
            localStorage.setItem("resume2LeftSectionOrder", JSON.stringify(defaultLeftSectionOrder));
          } else {
            setRightSectionOrder(defaultRightSectionOrder);
            localStorage.setItem("resume2RightSectionOrder", JSON.stringify(defaultRightSectionOrder));
          }
        }}>
        Reset {side === "left" ? "Left" : "Right"}
      </Button>
    </Box>
  );

  // ── Page & column layout styles ───────────────────────────────────────────
  const pageSx = {
    width:     `${page.width}px`,
    minHeight: `${page.height}px`,
    maxWidth:  `${page.width}px`,
    overflow:  "hidden",
    display:   "flex",
    flexDirection: "row",
    bgcolor:   "#ffffff",
    boxShadow: exporting ? "none" : "0 4px 20px rgba(0,0,0,0.15)",
    mx: "auto",
    mb: "24px",
    boxSizing: "border-box",
    fontFamily: font,
  };

  const sidebarSx = {
    width:    `${SIDE_W}px`,
    minWidth: `${SIDE_W}px`,
    maxWidth: `${SIDE_W}px`,
    minHeight: `${page.height}px`,
    overflow: "hidden",
    bgcolor:  color,
    color:    "white",
    p:        `${topBottomMargin}px ${Math.max(10, Math.round(sideMargins * 0.55))}px`,
    boxSizing: "border-box",
    display:  "flex",
    flexDirection: "column",
    alignItems: "center",
    flexShrink: 0,
  };

  const mainSx = {
    width:    `${MAIN_W}px`,
    minWidth: 0,
    minHeight: `${page.height}px`,
    overflow: "hidden",
    p:        `${topBottomMargin}px ${sideMargins}px`,
    boxSizing: "border-box",
  };

  const totalPages = Math.max(1, pages.length);

  return (
    <Box sx={{ position: "relative", fontFamily: font }}>
      {/* Controls */}
      {!exporting && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, flexWrap: "wrap", justifyContent: "center" }}>
          <IconButton onClick={() => setCurrentPage(p => Math.max(0, p - 1))} disabled={currentPage === 0} size="small">
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2">Page {currentPage + 1} of {totalPages}</Typography>
          <IconButton size="small" onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))} disabled={currentPage >= totalPages - 1}>
            <ChevronRight />
          </IconButton>
          {enableDragDrop && (
            <>
              <Button variant="contained" size="small" startIcon={<Reorder />} onClick={() => setArrangeDialogOpen(true)}>
                Arrange Sections
              </Button>
              <Button variant="outlined" size="small" color="secondary" onClick={resetAll}>
                Reset All
              </Button>
            </>
          )}
        </Box>
      )}

      {/* Hidden measurement container — renders ALL right sections to measure heights */}
      <Box
        ref={mainContentRef}
        sx={{
          position: "absolute", top: -9999, visibility: "hidden", pointerEvents: "none",
          width: `${MAIN_W}px`,
          p: `${topBottomMargin}px ${sideMargins}px`,
          boxSizing: "border-box",
          fontFamily: font, fontSize,
        }}
      >
        {rightSectionOrder.map(id => (
          <React.Fragment key={id}>{renderRight(id, false)}</React.Fragment>
        ))}
      </Box>

      {/* Resume Pages */}
      {(pages.length > 0 ? pages : [{ rightIds: null }]).map((pageContent, pageIdx) => (
        <Box
          key={pageIdx}
          className="resume-page"
          sx={{
            ...pageSx,
            display: exporting ? "flex" : (pageIdx === currentPage ? "flex" : "none"),
          }}
        >
          {/* SIDEBAR */}
          <Box sx={sidebarSx}>
            {pageIdx === 0 && (
              <Box sx={{ textAlign: "center", width: "100%", mb: secGap }}>
                <Typography sx={{ fontFamily: font, fontWeight: 800, fontSize: headingSize, color: "white", lineHeight: lh, ...OVF }}>
                  {(data.firstName || "").toUpperCase()} {(data.lastName || "").toUpperCase()}
                </Typography>
                {data.currentPosition && (
                  <Typography sx={{ ...sbTxt, mt: "3px" }}>{data.currentPosition}</Typography>
                )}
              </Box>
            )}
            {pageIdx === 0 && leftSectionOrder.map(id => {
              const content = renderLeft(id);
              return content ? <React.Fragment key={id}>{content}</React.Fragment> : null;
            })}

          </Box>

          {/* MAIN */}
          <Box sx={mainSx}>
            <Box sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
              {rightSectionOrder.map(id => (
                <React.Fragment key={id}>{renderRight(id, false, pageContent.rightIds)}</React.Fragment>
              ))}
            </Box>
          </Box>
        </Box>
      ))}

      {/* Arrange Sections Dialog */}
      <Dialog open={arrangeDialogOpen} onClose={() => setArrangeDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Reorder /> Arrange Resume Sections
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Drag to reorder or move sections between sidebar and main content.
          </Typography>
          <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
            <DragPanel side="left"  sections={leftSectionOrder} />
            <DragPanel side="right" sections={rightSectionOrder} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetAll} color="secondary">Reset All</Button>
          <Button variant="contained" onClick={() => setArrangeDialogOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Resume2;